import { Component, effect, inject, input, OnInit, signal } from '@angular/core';
import { MatListModule } from '@angular/material/list';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Medication, MedicationRepositoryService, ReportControl } from '@xmedical/core';
import { ReportControlEditorService } from '../../services/report-control-editor.service';

/**
 * MedicationListRendererComponent
 *
 * Renders a list of patient medications in a report.
 * Uses a refreshTrigger input signal instead of PatientEventsService.
 * The consuming app should increment refreshTrigger when medications change.
 */
@Component({
  selector: 'xmed-medication-list-renderer',
  standalone: true,
  imports: [
    MatListModule,
    MatFormFieldModule
  ],
  templateUrl: './medication-list-renderer.component.html',
  styleUrl: './medication-list-renderer.component.scss'
})
export class MedicationListRendererComponent implements OnInit {
  private medicationRepository = inject(MedicationRepositoryService);
  private editorService = inject(ReportControlEditorService);

  patientId = input.required<number>();
  control = input.required<ReportControl>();
  refreshTrigger = input<number>(0);

  medicationList = signal<Medication[]>([]);

  // Effect to reload when refreshTrigger changes
  private refreshEffect = effect(() => {
    const trigger = this.refreshTrigger();
    const pid = this.patientId();
    if (trigger > 0 && pid) {
      this.loadMedicationList();
    }
  });

  ngOnInit(): void {
    this.medicationList.set(this.control().medicationListValue || []);
    this.loadMedicationList();
  }

  private loadMedicationList(): void {
    this.medicationRepository.loadPatientMedication(this.patientId()).subscribe(medication => {
      const sorted = medication.sort((a, b) => a.createdDate > b.createdDate ? 1 : -1);
      this.medicationList.set(sorted);
      this.control().medicationListValue = sorted;
      this.editorService.triggerEvent();
    });
  }
}
